import { useEffect, useState } from 'react';
import { act } from '../../lib/actions';
import { api } from '../../lib/api';
import { loadReference } from '../../lib/reference';
import { Button } from '../ui';
import { useNova } from '../../lib/store';
import { Cell, Field, inputClass, RetentionField, Row, SectionHeader, Table, useLoad, when } from './kit';

interface Org {
  name: string;
  timezone: string;
  slug: string;
  branding: { crmUrl?: string } | null;
  recording_retention_days: number | null;
}

export function OrgSettings() {
  const [org, reload] = useLoad<Org>('/org');
  const [name, setName] = useState('');
  const [timezone, setTimezone] = useState('');
  const [crmUrl, setCrmUrl] = useState('');
  const [retention, setRetention] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!org) return;
    setName(org.name);
    setTimezone(org.timezone);
    setCrmUrl(org.branding?.crmUrl ?? '');
    setRetention(org.recording_retention_days);
  }, [org]);

  async function save() {
    setBusy(true);
    const ok = await act(() => api('PATCH', '/org', { name, timezone, crmUrl: crmUrl.trim(), recordingRetentionDays: retention }), 'Settings saved');
    setBusy(false);
    if (ok) {
      reload();
      void loadReference();
    }
  }

  const crmOk = crmUrl.trim() === '' || /^https:\/\//.test(crmUrl.trim());

  return (
    <>
      <SectionHeader title="Settings" lead="Your organization's name, time zone, the CRM shown to agents during calls, and how long recordings are kept." />
      <AddOns />
      <div className="max-w-[640px] rounded-xl border border-line bg-hull/60 p-5">
        <Field label="Organization name">{(id) => <input id={id} className={inputClass} value={name} onChange={(e) => setName(e.target.value)} />}</Field>
        <Field label="Time zone" hint="Used for today's numbers and for reports. Example: Africa/Nairobi.">
          {(id) => <input id={id} className={inputClass} value={timezone} onChange={(e) => setTimezone(e.target.value)} />}
        </Field>
        <Field
          label="CRM page for agents"
          hint="Shown in the CRM tab during calls. Nova fills in {caller_number}, {interaction_id} and {agent_email}. Leave empty to hide it."
        >
          {(id) => (
            <input
              id={id}
              className={inputClass}
              placeholder="https://crm.example.com/search?phone={caller_number}"
              value={crmUrl}
              onChange={(e) => setCrmUrl(e.target.value)}
            />
          )}
        </Field>
        {!crmOk ? <p className="mb-4 text-sm text-break">Use an https:// address; browsers block other pages inside Nova.</p> : null}
        <RetentionField
          value={retention}
          none="Forever"
          hint="For queues without their own setting, and calls without a queue such as outbound calls. Older recordings are deleted automatically."
          onChange={setRetention}
        />
        <Button tone="primary" onClick={() => void save()} disabled={busy || !crmOk || name.trim().length < 2}>
          {busy ? 'Saving…' : 'Save settings'}
        </Button>
      </div>
    </>
  );
}

interface AuditRow {
  id: number;
  action: string;
  entity: string;
  entity_id: string | null;
  after: Record<string, unknown> | null;
  at: string;
  actor: string | null;
}

const ACTION: Record<string, string> = {
  'user.create': 'added a user',
  'user.update': 'changed a user',
  'user.skills': 'changed skills of a user',
  'user.password_reset': 'reset a password',
  'queue.create': 'added a queue',
  'queue.update': 'changed a queue',
  'queue.delete': 'deleted a queue',
  'trunk.create': 'added a carrier',
  'trunk.delete': 'removed a carrier',
  'did.create': 'added a number',
  'did.update': 'changed where a number goes',
  'recording.purge': 'deleted old recordings (retention)',
  'call.monitor': 'listened to, whispered on or joined a call',
  'queue.emergency_close': 'closed a queue (emergency)',
  'queue.emergency_open': 'reopened a queue',
  'webhook.create': 'added a webhook',
  'webhook.update': 'changed a webhook',
  'webhook.delete': 'deleted a webhook',
  'webhook.retry': 'sent a webhook delivery again',
  'crm_lookup.update': 'changed the CRM lookup',
  'crm_lookup.delete': 'removed the CRM lookup',
  'did.delete': 'removed a number',
  'ivr.create': 'added an IVR flow',
  'ivr.rename': 'renamed an IVR flow',
  'ivr.publish': 'published an IVR flow',
  'ivr.rollback': 'rolled back an IVR flow',
  'ivr.restore': 'restored an IVR flow version to the draft',
  'ivr.delete': 'deleted an IVR flow',
  'prompt.create': 'uploaded a message',
  'prompt.delete': 'deleted a message',
  'agent.force_state': "changed an agent's status",
  'org.update': 'changed settings',
  'org.create': 'created the organization',
  'org.quotas': 'changed the licenses or agent limit',
  'org.suspend': 'suspended the organization',
  'org.reactivate': 'reactivated the organization',
  'user.password_change': 'changed their own password',
  'queue.add_agent': 'put an agent on a queue',
  'privacy.export': "downloaded a caller's data (privacy request)",
  'privacy.erase': 'erased a caller (privacy request)',
  'scheduled_report.create': 'scheduled a report email',
  'scheduled_report.update': 'changed a report email',
  'scheduled_report.delete': 'deleted a report email',
  'business_hours.create': 'added business hours',
  'business_hours.update': 'changed business hours',
  'business_hours.delete': 'deleted business hours',
  'skill.delete': 'deleted a skill',
  'chat_widget.create': 'added a livechat widget',
  'chat_widget.update': 'changed a livechat widget',
  'chat_widget.delete': 'deleted a livechat widget',
  'mailbox.create': 'added a mailbox',
  'mailbox.update': 'changed a mailbox',
  'mailbox.delete': 'deleted a mailbox',
  'whatsapp.create': 'connected a WhatsApp number',
  'qa.scorecard.create': 'added a scorecard',
  'qa.scorecard.update': 'changed a scorecard',
  'qa.scorecard.retire': 'retired a scorecard',
  'qa.review.submit': 'submitted a quality review',
  'qa.review.change': 'changed a submitted quality review',
  'qa.review.dispute': 'disputed a quality review',
  'qa.review.reply': 'answered a disputed review',
  'org.features': 'switched add-ons',
  'whatsapp_template.create': 'sent a WhatsApp template for approval',
  'whatsapp_template.delete': 'deleted a WhatsApp template',
  'whatsapp.update': 'changed a WhatsApp number',
  'whatsapp.delete': 'disconnected a WhatsApp number',
  'telegram.create': 'connected a Telegram bot',
  'telegram.update': 'changed a Telegram bot',
  'telegram.delete': 'disconnected a Telegram bot',
  'canned.create': 'added a canned response',
  'canned.update': 'changed a canned response',
  'canned.delete': 'deleted a canned response',
};

export function AuditLog() {
  const [rows] = useLoad<AuditRow[]>('/audit-log');
  return (
    <>
      <SectionHeader title="Audit log" lead="Every change made by admins and supervisors, newest first. It cannot be edited." />
      <Table head={['When', 'Who', 'What', 'Details']} empty={rows?.length === 0 && 'Nothing yet.'}>
        {(rows ?? []).map((r) => (
          <Row key={r.id}>
            <Cell className="whitespace-nowrap text-ink-2">{when(r.at)}</Cell>
            <Cell>{r.actor ?? 'System'}</Cell>
            <Cell>{ACTION[r.action] ?? r.action.replace(/[._]/g, ' ')}</Cell>
            <Cell className="max-w-[420px] truncate text-ink-3">{describe(r.after)}</Cell>
          </Row>
        ))}
      </Table>
    </>
  );
}

function describe(after: Record<string, unknown> | null): string {
  if (!after) return '';
  return Object.entries(after)
    // "by" is an account id older provider entries carried; the Who column names them now
    .filter(([k, v]) => v !== undefined && v !== null && !['org_id', 'id', 'created_at', 'by'].includes(k) && typeof v !== 'object')
    .slice(0, 4)
    .map(([k, v]) => `${k.replace(/_/g, ' ')}: ${String(v)}`)
    .join(', ');
}

/** Add-ons the provider switches on for this organization. */
function AddOns() {
  const qa = useNova((s) => Boolean(s.features.qa));
  return (
    <div className="mb-4 max-w-[640px] rounded-xl border border-line bg-hull/60 p-4" aria-label="Add-ons">
      <p className="text-sm font-medium">Add-ons</p>
      <p className="mt-1 text-sm text-ink-2">
        Quality (QA): <span className={qa ? 'font-medium text-available' : 'text-ink-3'}>{qa ? 'on' : 'off'}</span>. Your provider switches add-ons on and off.
        {qa ? ' Scorecards are under Admin > Quality; reviews under Reports > Quality.' : ''}
      </p>
    </div>
  );
}
